import React, { useEffect, useState } from 'react';
import { useUser } from './User';
import {
  getOemHealth
} from '../libs/db-lib';
import { OemServiceIssue } from '../types';

export interface OemHealthInterface {
  isOemHealthLoading: boolean;
  oemList: any[];
  handleRefresh: (overrideIsOemHealthLoading: boolean) => any;
  oemServiceIssues: OemServiceIssue[];
}

export const OemHealthContext = React.createContext<OemHealthInterface>({
  isOemHealthLoading: true,
  oemList: [],
  handleRefresh: (overrideIsOemHealthLoading) => null,
  oemServiceIssues: [],
});

export const useOemHealth = () => {
  return React.useContext(OemHealthContext);
};

type OemHealthProviderType = {
  children: JSX.Element;
};

export function OemHealthProvider({ children }: OemHealthProviderType) {
  const { user } = useUser();

  const [isOemHealthLoading, setIsOemHealthLoading] = useState<boolean>(true);
  const [oemList, setOemList] = useState<any[]>([]);

  const [oemServiceIssues, setOemServiceIssues] = useState<OemServiceIssue[]>([]);

  useEffect(() => {
    if (user) {
      handleRefresh(true);
      const refreshInterval = setInterval(() => {
        handleRefresh(false);
      }, 300000); // refreshing every 5 minutes
      return () => {
        clearInterval(refreshInterval);
      };
    } else {
      setOemList([]);
    }
  }, [user]);

  const handleRefresh = async (overrideIsOemHealthLoading: boolean) => {
    if (!user) {
      return;
    }

    if (overrideIsOemHealthLoading) {
      setIsOemHealthLoading(true);
    }

    const response = await getOemHealth();

    if (!response?.error) {
      const oemList = getOemList(response?.oemList || { oemList: [] })

      setOemList(oemList);

      const currentOemServiceIssues = [...oemServiceIssues];

      oemList.forEach((oem: any) => {
        let hasNoIssue: boolean = true;

        hasNoIssue = (((oem?.last24Hours ?? [])[23]?.serviceLevel ?? '').toLowerCase() || 'good') === 'good';

        if (!hasNoIssue) {
          const status = oem.last24Hours[23];
          const issue = { oemID: oem.oemID, oemName: oem.oemName, serviceLevel: status.serviceLevel };
          const i = currentOemServiceIssues.findIndex(serviceIssue => serviceIssue.oemID === oem.oemID);
          i > -1 ? currentOemServiceIssues[i] = issue : currentOemServiceIssues.push(issue);
        } else {
          const i = currentOemServiceIssues.findIndex(serviceIssue => serviceIssue.oemID === oem.oemID);
          if (i > -1) currentOemServiceIssues.splice(i, 1);
        }
      });

      setOemServiceIssues([...currentOemServiceIssues]);
    }

    setIsOemHealthLoading(false);
    return response;
  };

  const getOemList = (data: any) => {
    const oemList = (data || []).map((oem: any) => {
      return {
        oemID: oem?.oemID,
        oemName: oem?.oemName,
        regionName: oem?.regionName,
        last24Hours: oem?.last24Hours,
        last7Days: oem?.last7Days,
        filterString: `${oem?.oemName} ${oem?.regionName}`.toLowerCase(),
      };
    });
    return oemList;
  };


  const provider = {
    isOemHealthLoading,
    oemList,
    handleRefresh,
    oemServiceIssues,
  };

  return <OemHealthContext.Provider value={provider}>{children}</OemHealthContext.Provider>;
}
